import React, { useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartData, ChartOptions } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Maximize2 } from 'lucide-react';
import { CardWithHeader } from '../../../../ui/CardWithHeader';
import { ExpandModal } from '../../../../ui/ExpandModal';
import { NoDataInPeriod } from '../../../../ui/NoDataInPeriod';
import { cn } from '../../../../../lib/utils';
import { tertiaryText } from '../../../../../styling/stylingHelper';
import { eur } from '../../../../orderhistory/utils';

export interface WasteDonutItem {
  label: string;
  color: string;
  value: number;
  percentage: number;
}

interface WasteDonutCardProps {
  title: string;
  data: WasteDonutItem[];
  /** Unique id for the external chart.js tooltip element. */
  tooltipId: string;
}

/**
 * Donut + wrap legend card, reusing the all-restaurants "Spend by category" handling
 * (chart.js doughnut, external coloured tooltip, capped scrollable legend, maximize →
 * ExpandModal). Generic over the metric so it serves both waste-by-category and
 * waste-by-supplier.
 */
export const WasteDonutCard: React.FC<WasteDonutCardProps> = ({ title, data, tooltipId }) => {
  const { t } = useTranslation();
  const [maximized, setMaximized] = useState(false);
  const colors = data.map((d) => d.color);

  ChartJS.register(ArcElement, Tooltip, Legend);

  const chartData: ChartData<'doughnut'> = {
    labels: data.map((d) => d.label),
    datasets: [{ data: data.map((d) => d.percentage), backgroundColor: colors, borderWidth: 0, borderRadius: 10, spacing: 6, hoverOffset: 2 }],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: (context) => {
          let el = document.getElementById(tooltipId);
          if (!el) {
            el = document.createElement('div');
            el.id = tooltipId;
            // `el` is appended to <body>, which is a descendant of <html> — the element
            // that `useAppSettingsStore` toggles the `dark` class on — so plain `dark:`
            // classes here react to the theme live, same as any React-rendered element.
            el.innerHTML = `<div class="bg-white dark:bg-background-dark border border-[#F5F5F5] dark:border-border-dark rounded-lg p-2 shadow-lg"><span class="tooltip-text"></span></div>`;
            document.body.appendChild(el);
          }
          const tt = context.tooltip;
          if (tt.opacity === 0) {
            el.style.opacity = '0';
            return;
          }
          if (tt.body) {
            const span = el.querySelector('.tooltip-text') as HTMLElement;
            const i = tt.dataPoints[0].dataIndex;
            span.innerHTML = `${data[i].label} · ${eur(data[i].value)}`;
            span.style.color = colors[i];
          }
          const pos = context.chart.canvas.getBoundingClientRect();
          el.style.opacity = '1';
          el.style.position = 'absolute';
          el.style.left = pos.left + window.pageXOffset + tt.caretX - 30 + 'px';
          el.style.top = pos.top + window.pageYOffset + tt.caretY - 40 + 'px';
          el.style.pointerEvents = 'none';
          el.style.zIndex = '100';
          el.style.transition = 'all 0.1s ease';
          el.style.transform = 'translateX(-50%)';
        },
      },
    },
  };

  const renderBody = (heightClass: string, legendMaxWidthClass: string, legendMaxHeightClass: string) => {
    if (data.length === 0) return <NoDataInPeriod />;
    return (
      <div className="w-full">
        <div className={cn('relative w-full flex items-center justify-center select-none mt-6', heightClass)}>
          <Doughnut data={chartData} options={options} />
        </div>
        <div className={cn('mt-6 mb-3 flex items-center flex-wrap justify-center mx-auto gap-3 overflow-y-auto custom-scroll', legendMaxWidthClass, legendMaxHeightClass)}>
          {data.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-[3px]" style={{ backgroundColor: colors[i] }} />
              <span className="text-[#475467] dark:text-textSecondary-dark text-[13px] leading-5 font-medium whitespace-nowrap">
                {item.label} · {eur(item.value)} ({item.percentage.toFixed(1)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const maximizeButton = (
    <button
      type="button"
      onClick={() => setMaximized(true)}
      title={t('dashboard.maximize')}
      aria-label={t('dashboard.maximize')}
      className={cn('p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition', tertiaryText)}
    >
      <Maximize2 className="w-4 h-4" />
    </button>
  );

  return (
    <>
      <CardWithHeader title={title} headerActionsRight={maximizeButton}>
        {renderBody('h-[200px] sm:h-[240px]', 'max-w-[424px]', 'max-h-[180px]')}
      </CardWithHeader>
      <ExpandModal isOpen={maximized} onClose={() => setMaximized(false)} title={title} maxWidth="max-w-2xl">
        {renderBody('h-[360px] sm:h-[440px]', 'max-w-full', 'max-h-[260px]')}
      </ExpandModal>
    </>
  );
};

export default WasteDonutCard;
